import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import { listRules } from '../../api/rules';
import { formatTimestamp, getStoredTimeFormat } from '../../utils/timeFormat';
import { ResourceLink } from '../../components/ResourceLink';

interface DeadLetterTabProps {
  storeName: string;
}

interface RuleItem {
  name: string;
  ruleId: string;
  sourceEventType: string;
  target: { type: string; arn: string };
  dlqArn?: string;
}

export function DeadLetterTab({ storeName }: DeadLetterTabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rules', storeName],
    queryFn: () => listRules(storeName),
  });

  const rules: RuleItem[] = ((data?.data as any)?.rules || []).filter((r: RuleItem) => r.dlqArn);

  return (
    <SpaceBetween size="m">
      <Table
        header={
          <Header
            description="Dead letter queues capture events that failed delivery to subscription targets. Each subscription has its own DLQ."
          >
            Failed deliveries
          </Header>
        }
        loading={isLoading}
        loadingText="Loading dead letter queues"
        items={rules}
        columnDefinitions={[
          {
            id: 'rule',
            header: 'Subscription',
            cell: (item) => <code>{item.name}</code>,
          },
          {
            id: 'source',
            header: 'Source event',
            cell: (item) => item.sourceEventType,
          },
          {
            id: 'target',
            header: 'Target',
            cell: (item) => (
              <ResourceLink arn={item.target.arn} />
            ),
          },
          {
            id: 'dlq',
            header: 'DLQ',
            cell: (item) => item.dlqArn ? <ResourceLink arn={item.dlqArn} /> : '—',
          },
        ]}
        variant="embedded"
        empty={
          <Box textAlign="center" padding="l" color="text-body-secondary">
            No subscriptions with dead letter queues configured.
          </Box>
        }
      />
      <Box color="text-body-secondary" fontSize="body-s">
        To inspect DLQ messages, use the SQS console or AWS CLI. DLQ ARNs are shown above for each subscription.
        Future: inline DLQ message viewer with replay capability.
      </Box>
    </SpaceBetween>
  );
}
